import { CloseButton } from '@eventbrite/eds-modal';
import { gettext } from '@eventbrite/i18n';
import { InlineAlert, MegaphoneIcon } from '@eventbrite/marmalade';
import classnames from 'classnames';
import React from 'react';
import type { InfiniteCalendarSeriesDispatcher } from '../../actions/infiniteCalendarSeries';
import { PageTemplate, TemplateProps } from '../../containers/template';
import {
    AccordionSection,
    CalendarType,
    type InfiniteCalendarSeriesNamespace,
} from '../../state/infiniteCalendarSeries';
import {
    DEFAULT_STYLES,
    getStyles,
    REBRANDING_STYLES,
} from '../../utils/rebrand';
import { ConnectedEventInfoCard } from '../EventInfoCard/ConnectedEventInfoCard';
import {
    AccordionPanel,
    CalendarContainer,
    getFormattedTime,
    TicketsAccordionLoadingSkeleton,
} from '../TimedEntrySeries';
import { DateSvg } from '../TimedEntrySeries/assests/DateSvg';
import { RebrandedAlertSvg } from '../TimedEntrySeries/assests/RebrandedAlertSvg';
import { RebrandedDateSvg } from '../TimedEntrySeries/assests/RebrandedDateSvg';
import { RebrandedTicketSvg } from '../TimedEntrySeries/assests/RebrandedTicketSvg';
import { RebrandedTimeSvg } from '../TimedEntrySeries/assests/RebrandedTimeSvg';
import { TicketSvg } from '../TimedEntrySeries/assests/TicketSvg';
import { TimeSvg } from '../TimedEntrySeries/assests/TimeSvg';
import { TimeSlotContainer } from '../TimedEntrySeries/TimeSlotContainer';
import { TimeSlotLoadingSkeleton } from '../TimedEntrySeries/TimeSlotContainer/TimeSlotLoadingSkeleton/TimeSlotLoadingSkeleton';
import commonStyles from './InfiniteCalendarSeries.common.module.scss';
import defaultStyles from './InfiniteCalendarSeries.module.scss';
import rebrandStyles from './InfiniteCalendarSeries.rebranded.module.scss';
import {
    getAllSessionsMinPrice,
    getCurrentTimezoneUnavailableAlert,
    getDisplayDate,
    getPriceWithCurrencySymbol,
    getUnavailableEventAlert,
    isAccordionSectionOpen,
} from './utils';

const stylesModules = {
    [DEFAULT_STYLES]: defaultStyles,
    [REBRANDING_STYLES]: rebrandStyles,
};
export interface InfiniteCalendarSeriesProps
    extends InfiniteCalendarSeriesNamespace,
        InfiniteCalendarSeriesDispatcher {}

export const InfiniteCalendarSeries: React.FC<InfiniteCalendarSeriesProps> = ({
    activeSection,
    calendarDates,
    calendarType,
    children,
    compactCalendarData,
    currentTimezone,
    currency,
    eventDateOverride,
    eventStatus: eventStatusMessageCode,
    firstMonth,
    isAppRebrandActive,
    isEventOnline,
    isModal,
    isNativeApp,
    isTicketSectionLoading,
    isTimeSectionLoading,
    lastMonth,
    locale,
    onDateSelect,
    onPageClose,
    onMonthLoad,
    onTimeSelect,
    onToggleSection,
    resetTimeSelection,
    scrollCalendarData,
    selectedDate,
    selectedSessionId,
    selectedTime,
    shouldShowTicketError,
    shouldShowTimeError,
    templateProps,
    timeSlots,
    timezone,
}) => {
    const theme = getStyles(isAppRebrandActive);
    const styles = stylesModules[theme];

    const currentTimezoneUnavailableAlert =
        !currentTimezone && isEventOnline
            ? getCurrentTimezoneUnavailableAlert(timezone)
            : undefined;
    const eventStatusAlert =
        eventStatusMessageCode &&
        getUnavailableEventAlert(eventStatusMessageCode);

    const inlineAlertDescription =
        eventStatusAlert || currentTimezoneUnavailableAlert;

    const {
        TicketsAccordionIcon,
        CalendarAccordionIcon,
        TimeAccordionIcon,
        BannerIcon,
    } = isAppRebrandActive
        ? {
              TicketsAccordionIcon: RebrandedTicketSvg,
              CalendarAccordionIcon: RebrandedDateSvg,
              TimeAccordionIcon: RebrandedTimeSvg,
              BannerIcon: RebrandedAlertSvg,
          }
        : {
              TicketsAccordionIcon: TicketSvg,
              CalendarAccordionIcon: DateSvg,
              TimeAccordionIcon: TimeSvg,
              BannerIcon: MegaphoneIcon,
          };
    const sessionData =
        calendarType === CalendarType.compactWithSession
            ? calendarDates
            : timeSlots;

    const allSessionsMinPrice = getAllSessionsMinPrice(
        sessionData as Array<{ minPrice?: string }>,
    );

    const minPriceForSession = getPriceWithCurrencySymbol(
        allSessionsMinPrice,
        currency,
    );

    const tickets = isTicketSectionLoading ? (
        <TicketsAccordionLoadingSkeleton />
    ) : (
        children
    );
    const timeSlotsSection = isTimeSectionLoading ? (
        <TimeSlotLoadingSkeleton />
    ) : (
        <TimeSlotContainer
            isAppRebrandActive={isAppRebrandActive}
            currency={currency}
            locale={locale}
            onTimeSelect={onTimeSelect}
            selectedSessionId={selectedSessionId}
            showFooter={!eventStatusAlert}
            timeSlotData={timeSlots || []}
            timezone={timezone}
        />
    );
    const displayDate: string = getDisplayDate({
        activeSection,
        calendarType,
        date: selectedDate || '',
        locale,
        timezone,
    });
    const displayTime: string =
        activeSection === AccordionSection.time ||
        !selectedTime ||
        activeSection === AccordionSection.date
            ? ''
            : getFormattedTime(selectedTime, locale, timezone);

    const ticketErrorText = !selectedDate
        ? gettext('Select day and time to check tickets').toString()
        : gettext('Select a time to check tickets').toString();

    const shouldDisplayChooseOptionsTitleAndCloseButton =
        isModal || isNativeApp;

    const isDateSectionOpen = isAccordionSectionOpen(
        activeSection,
        AccordionSection.date,
    );

    const isTicketsSectionOpen = isAccordionSectionOpen(
        activeSection,
        AccordionSection.tickets,
    );

    const dateSectionTitle =
        calendarType === CalendarType.compactWithSession
            ? gettext('Date and time').toString()
            : gettext('Date').toString();

    const isSingleDayEvent = calendarType === CalendarType.singleDayEvents;

    const isTimeSectionOpen = isAccordionSectionOpen(
        activeSection,
        AccordionSection.time,
    );

    const handleSectionToggle = (selectedSection: AccordionSection) => {
        if (eventStatusAlert) {
            return;
        }
        onToggleSection({
            activeSection: selectedSection,
            selectedDate,
            selectedTime,
            calendarType,
        });
    };

    const banner = inlineAlertDescription ? (
        <InlineAlert
            className={classnames(commonStyles.banner, styles.banner)}
            description={inlineAlertDescription}
            variant="opportunity"
            icon={<BannerIcon className={styles.bannerIcon} />}
        />
    ) : null;

    const timeAccordionPanel = (
        <AccordionPanel
            title={gettext('Time').toString()}
            isExpanded={isTimeSectionOpen && !eventStatusAlert}
            onClick={() => handleSectionToggle(AccordionSection.time)}
            icon={<TimeAccordionIcon />}
            displayDateTime={displayTime}
            error={shouldShowTimeError}
            errorText={gettext('Select a day to check time slots').toString()}
            shouldClubAccordionDateAndTimeSections={isTicketsSectionOpen}
            isAppRebrandActive={isAppRebrandActive}
        >
            {timeSlotsSection}
        </AccordionPanel>
    );

    const dateAndTimeSections = isSingleDayEvent ? (
        <>
            <ConnectedEventInfoCard
                onChange={
                    selectedSessionId
                        ? () => {
                              handleSectionToggle(AccordionSection.time);
                              resetTimeSelection();
                          }
                        : undefined
                }
                eventDateOverride={
                    !selectedSessionId || isTicketSectionLoading
                        ? eventDateOverride
                        : undefined
                }
                showEventPrice={false}
            />
            {!selectedSessionId ? timeAccordionPanel : null}
        </>
    ) : (
        <>
            <AccordionPanel
                title={dateSectionTitle}
                isExpanded={isDateSectionOpen && !eventStatusAlert}
                onClick={() => {
                    handleSectionToggle(AccordionSection.date);
                }}
                icon={<CalendarAccordionIcon />}
                displayDateTime={displayDate}
                isAppRebrandActive={isAppRebrandActive}
            >
                <CalendarContainer
                    calendarType={calendarType}
                    scrollCalendarData={scrollCalendarData}
                    compactCalendarData={compactCalendarData}
                    locale={locale}
                    onMonthLoad={(monthAndYear) => {
                        onMonthLoad(
                            monthAndYear.month,
                            monthAndYear.year,
                            timezone,
                        );
                    }}
                    onDateSelect={onDateSelect}
                    isAppRebrandActive={isAppRebrandActive}
                    initialSelectedDate={selectedDate || ''}
                    firstMonth={firstMonth}
                    lastMonth={lastMonth}
                    showFooter={!eventStatusAlert}
                    calendarDates={calendarDates}
                    currency={currency}
                />
            </AccordionPanel>
            {calendarType !== CalendarType.compactWithSession ? (
                <>{timeAccordionPanel}</>
            ) : null}
        </>
    );

    const header = shouldDisplayChooseOptionsTitleAndCloseButton ? (
        <header className={commonStyles.headerContainer}>
            <h1 className={styles.headerText}>
                {gettext('Choose options').toString()}
            </h1>
            <CloseButton
                customClassName={commonStyles.closeButton}
                onClose={onPageClose}
            />
        </header>
    ) : null;

    return (
        <PageTemplate
            notificationOptions={[]}
            templateProps={templateProps as TemplateProps}
            isAppRebrandActive={isAppRebrandActive}
            containerClassName={commonStyles.pageTemplateContainer}
            extraPaneProps={{
                isSeriesInfiniteCalendar: true,
                isTicketSelectionPage: false,
                showCartIconInPane: false,
            }}
        >
            <div
                className={classnames(
                    commonStyles.container,
                    styles.container,
                    {
                        [commonStyles.modalContainer]:
                            shouldDisplayChooseOptionsTitleAndCloseButton,
                        [commonStyles.expandedTicketsSectionContentContainer]:
                            isTicketsSectionOpen,
                    },
                )}
            >
                {header}
                <div
                    className={classnames(commonStyles.contentContainer, {
                        [commonStyles.modalContentContainer]:
                            shouldDisplayChooseOptionsTitleAndCloseButton,
                    })}
                >
                    {banner}
                    {dateAndTimeSections}
                    <AccordionPanel
                        collapsibleContainerClassName={
                            commonStyles.ticketsContentContainer
                        }
                        icon={<TicketsAccordionIcon />}
                        error={shouldShowTicketError}
                        errorText={ticketErrorText}
                        isAppRebrandActive={isAppRebrandActive}
                        isExpanded={isTicketsSectionOpen}
                        onClick={() =>
                            handleSectionToggle(AccordionSection.tickets)
                        }
                        title={gettext('Tickets').toString()}
                        sessionMinPrice={
                            !isTicketsSectionOpen
                                ? minPriceForSession
                                : undefined
                        }
                    >
                        {tickets}
                    </AccordionPanel>
                </div>
            </div>
        </PageTemplate>
    );
};
